import { Injectable, inject, Signal, computed } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { EMPTY, Observable } from "rxjs";

import { LanguageService, MedicationActions, Model } from "app/shared";
import {
	AdministrationMedicationIncidentType,
	CreateAdministrationMedicationIncidentBody,
	AdministrationMedicationIncidentDetail,
	ResolvedAdministrationMedicationIncidentDetail,
	Responsible,
	AdministrationMethod,
	AdministrationMedicationIncident,
} from "app/shared/types";
import { selectAdministrationMethods } from "app/shared/state/selectors/administration-methods.selectors";
import { LanguagePipe } from "app/shared/pipes";
import { OnlineModel } from "app/shared/models/online.model";

import { AWSHttpClient } from "@intocare-medapp/aws/http";
import { catchError } from "rxjs/operators";
import { Option } from "../../components/single-select-list/single-select-list.component";
import { generateDateOptions } from "../../util/date-options";

@Injectable({
	providedIn: "root",
})
export class AdministrationMedicationIncidentModel extends Model {
	private readonly store = inject(Store);
	private readonly translateService = inject(TranslateService);
	private readonly languageService = inject(LanguageService);
	private readonly languagePipe = inject(LanguagePipe);
	private readonly onlineModel = inject(OnlineModel);
	private readonly httpClient = inject(AWSHttpClient);

	private readonly administrationMethods: Signal<AdministrationMethod[]> =
		this.store.selectSignal(selectAdministrationMethods);

	public readonly administrationMethodOptions = computed(() =>
		this.administrationMethods().map((method) => ({
			label: this.languagePipe.transform(method.name),
			value: method,
		})),
	);

	public createAdministrationMedicationIncident(incident: CreateAdministrationMedicationIncidentBody) {
		return this.performAsyncAction(MedicationActions.createAdministrationMedicationIncident(incident));
	}

	public readonly responsibleOptions: Option<Responsible>[] = [
		{
			label: "medication.administration_incidents.responsibleTypes.CAREGIVER",
			icon: "fa-user-md",
			value: Responsible.CAREGIVER,
		},
		{
			label: "medication.administration_incidents.responsibleTypes.RESIDENT",
			icon: "fa-user-circle-o",
			value: Responsible.RESIDENT,
		},
		{
			label: "medication.administration_incidents.responsibleTypes.THIRD_PARTY",
			icon: "fa-user-o",
			value: Responsible.THIRD_PARTY,
		},
		{
			label: "medication.administration_incidents.responsibleTypes.UNKNOWN",
			icon: "fa-question-circle",
			value: Responsible.UNKNOWN,
		},
	].map((option) => ({ ...option, label: this.translateService.instant(option.label) }));

	public readonly incidentTypeOptions: Option<AdministrationMedicationIncidentType>[] = [
		{
			label: "medication.administration_incidents.incidentTypes.DOSE",
			icon: "fa-flask",
			value: AdministrationMedicationIncidentType.DOSE,
		},
		{
			label: "medication.administration_incidents.incidentTypes.TIME",
			icon: "fa-clock-o",
			value: AdministrationMedicationIncidentType.TIME,
		},
		{
			label: "medication.administration_incidents.incidentTypes.MEDICATION",
			icon: "fae-medicine",
			value: AdministrationMedicationIncidentType.MEDICATION,
		},
		{
			label: "medication.administration_incidents.incidentTypes.METHOD",
			icon: "fa-hand-paper-o",
			value: AdministrationMedicationIncidentType.METHOD,
		},
		{
			label: "medication.administration_incidents.incidentTypes.NOT_ADMINISTERED",
			icon: "fa-exclamation-circle",
			value: AdministrationMedicationIncidentType.NOT_ADMINISTERED,
		},
	].map((option) => ({ ...option, label: this.translateService.instant(option.label) }));

	public generateDateOptions(): Option<string>[] {
		return generateDateOptions(this.languageService.language(), 4);
	}

	private getAdministrationMethod(id: string): AdministrationMethod | undefined {
		const methods = this.administrationMethods();
		return methods?.find((method) => method.id === id);
	}

	public resolveAdministrationMethods(
		details: AdministrationMedicationIncidentDetail[],
	): ResolvedAdministrationMedicationIncidentDetail[] {
		// @ts-expect-error TS2322
		return details.map((detail) => {
			return {
				...detail,
				...("plannedMethod" in detail && { plannedMethod: this.getAdministrationMethod(detail.plannedMethod) }),
				...("actualMethod" in detail && { actualMethod: this.getAdministrationMethod(detail.actualMethod) }),
			};
		});
	}

	public checkOnlineShowDialog(navigationRoute?: string[]): Observable<boolean> {
		return this.onlineModel.checkOnlineShowDialog({
			dialogData: {
				title: "medication.offline.title",
				content: "medication.offline.content",
				cancelButtonText: "medication.offline.cancel_button",
			},
			navigationRoute,
			disableClose: true,
		});
	}

	public getAdministrationMedicationIncident(id: string) {
		return this.httpClient.get<AdministrationMedicationIncident>(`medication/incidents/${id}`).pipe(
			catchError(() => {
				return EMPTY;
			}),
		);
	}
}
